import {
  BatchForm,
  DeliveredType,
  QuoteHistoryItem,
} from "../types";
import {
  STANDARD_PACKAGE_PRESETS,
  STRESS_PACKAGE_PRESETS,
  packagesToTextareaValue,
} from "../presets";
import { parseCpList, parsePackages } from "../parsers";
import { quoteRates } from "../api";
import { LynxPackField } from "./LynxPackField";
import { LynxPackTextArea } from "./LynxPackTextArea";
import { DeliveryTypeSelect } from "./DeliveryTypeSelect";
import { PresetButtons } from "./PresetButtons";
import Button from "../../../components/ui/Button";

type BatchQuotePanelProps = {
  batchForm: BatchForm;
  setBatchForm: React.Dispatch<React.SetStateAction<BatchForm>>;
  loading: boolean;
  batchLoading: boolean;
  setBatchLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
  batchProgress: string;
  setBatchProgress: React.Dispatch<React.SetStateAction<string>>;
  setHistory: React.Dispatch<React.SetStateAction<QuoteHistoryItem[]>>;
};

export function BatchQuotePanel({
  batchForm,
  setBatchForm,
  loading,
  batchLoading,
  setBatchLoading,
  setError,
  batchProgress,
  setBatchProgress,
  setHistory,
}: BatchQuotePanelProps) {
  const canBatchQuote =
    batchForm.postalCodeOrigin.trim() &&
    batchForm.postalCodeDestinations.trim() &&
    batchForm.packages.trim();

  const handleBatchChange = (
    field: keyof BatchForm,
    value: string | DeliveredType
  ) => {
    setBatchForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const appendPackages = (packages: typeof STANDARD_PACKAGE_PRESETS) => {
    const current = batchForm.packages.trim();
    const next = packagesToTextareaValue(packages);

    setBatchForm((prev) => ({
      ...prev,
      packages: current ? `${current}\n${next}` : next,
    }));
  };

  const replacePackages = (packages: typeof STANDARD_PACKAGE_PRESETS) => {
    setBatchForm((prev) => ({
      ...prev,
      packages: packagesToTextareaValue(packages),
    }));
  };

  const handleBatchQuote = async () => {
    setError("");
    setBatchProgress("");
    setBatchLoading(true);

    try {
      const cpDestinations = parseCpList(batchForm.postalCodeDestinations);
      const packages = parsePackages(batchForm.packages);
      const total = cpDestinations.length * packages.length;

      if (total === 0) {
        throw new Error("No hay combinaciones para cotizar.");
      }

      const newItems: QuoteHistoryItem[] = [];
      let done = 0;

      for (const postalCodeDestination of cpDestinations) {
        for (const dimensions of packages) {
          done += 1;

          setBatchProgress(
            `Cotizando ${done}/${total}: ${batchForm.postalCodeOrigin.trim()} → ${postalCodeDestination} · ${
              dimensions.weight
            }g · ${dimensions.height}x${dimensions.width}x${dimensions.length}cm`
          );

          const baseItem: QuoteHistoryItem = {
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            postalCodeOrigin: batchForm.postalCodeOrigin.trim(),
            postalCodeDestination,
            deliveredType: batchForm.deliveredType,
            weight: String(dimensions.weight),
            height: String(dimensions.height),
            width: String(dimensions.width),
            length: String(dimensions.length),
            results: [],
            mode: "batch",
          };

          try {
            const cotizacion = await quoteRates({
              postalCodeOrigin: batchForm.postalCodeOrigin.trim(),
              postalCodeDestination,
              deliveredType: batchForm.deliveredType,
              weight: dimensions.weight,
              height: dimensions.height,
              width: dimensions.width,
              length: dimensions.length,
              mode: "batch",
              label: dimensions.label,
            });

            newItems.push({
              ...baseItem,
              id: cotizacion.id ?? baseItem.id,
              createdAt: cotizacion.createdAt,
              label: dimensions.label,
              results: cotizacion.rates,
              validTo: cotizacion.validTo,
              volumeCm3: cotizacion.volumeCm3,
              aforoKg: cotizacion.aforoKg,
              billableKg: cotizacion.billableKg,
              customerId: cotizacion.customerId,
              sinTarifas: cotizacion.sinTarifas,
            });
          } catch (err) {
            newItems.push({
              ...baseItem,
              error: err instanceof Error ? err.message : "Error inesperado.",
            });
          }
        }
      }

      setHistory((prev) => [...newItems.reverse(), ...prev]);
      setBatchProgress(`Listo. Se procesaron ${total} combinaciones.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error inesperado.");
    } finally {
      setBatchLoading(false);
    }
  };

  // Sin tarjeta propia: la página lo monta dentro del Card "Cotizar", que
  // alterna entre este panel y el uno a uno con un switch.
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 13 }}>
        Ejecuta una matriz: un CP origen contra muchos CP destino y muchos
        paquetes.
      </p>
        <LynxPackField
          label="CP origen"
          value={batchForm.postalCodeOrigin}
          onChange={(value) => handleBatchChange("postalCodeOrigin", value)}
          placeholder="Ej: 1706"
        />

        <LynxPackTextArea
          label="CP destinos"
          value={batchForm.postalCodeDestinations}
          onChange={(value) =>
            handleBatchChange("postalCodeDestinations", value)
          }
          rows={5}
          helperText="Separar por enter, coma o punto y coma."
        />

        <DeliveryTypeSelect
          value={batchForm.deliveredType}
          onChange={(value) => handleBatchChange("deliveredType", value)}
          allowAll
        />

        <LynxPackTextArea
          label="Paquetes"
          value={batchForm.packages}
          onChange={(value) => handleBatchChange("packages", value)}
          rows={8}
          helperText="Formato por línea: peso,alto,ancho,largo. Ej: 1000,10,20,30"
        />

        <PresetButtons
          title="Paquetes estándar"
          packages={STANDARD_PACKAGE_PRESETS}
          onAppendPackages={appendPackages}
          onReplacePackages={replacePackages}
        />

        <PresetButtons
          title="Stress test: límites de peso y volumen"
          packages={STRESS_PACKAGE_PRESETS}
          onAppendPackages={appendPackages}
          onReplacePackages={replacePackages}
        />

        {batchProgress && (
          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: 14,
              background: "var(--surface-alt)",
              color: "var(--text-primary)",
              fontSize: 13,
            }}
          >
            {batchProgress}
          </div>
        )}

        <Button
          variant="primary"
          fullWidth
          onClick={handleBatchQuote}
          disabled={!canBatchQuote || loading || batchLoading}
        >
          {batchLoading ? "Cotizando lote..." : "Cotizar lote"}
        </Button>
    </div>
  );
}